import Decimal from 'decimal.js';

import { toMoney } from 'utils/caseing';

export type DollarsString = string;
export type Dollars = Decimal;

export const parseDollarsString = (dollars?: DollarsString): Dollars => {
    if (!dollars) {
        return new Decimal(0);
    }

    if (!dollars.includes('.')) {
        return new Decimal(0);
    }

    return new Decimal(dollars);
};

export const toDollarsString = (dollars: Dollars): DollarsString => {
    return dollars.toFixed(4);
};

export const inversePriceDollars = (priceDollars: Dollars): Dollars => {
    return new Decimal(1).minus(priceDollars);
};

export const dollarsFromCents = (cents: number): Dollars => {
    return new Decimal(cents).dividedBy(100);
};

/**
 * Convert a precise `DollarsString` to cents as a JS number, preserving sub-cent
 * precision (e.g. "0.9950" -> 99.5). Returns `null` when the dollars string is
 * absent or malformed. Use when there is no integer-cents companion field, so
 * "field omitted" must be distinguishable from a real `0`.
 */
export const centsFromDollarsStringOrNull = (dollars: DollarsString | undefined): number | null => {
    if (typeof dollars !== 'string' || !dollars.includes('.')) {
        return null;
    }
    try {
        return new Decimal(dollars).times(100).toNumber();
    } catch {
        return null;
    }
};

/**
 * Like `centsFromDollarsStringOrNull`, but falls back to the legacy integer-cents
 * field when the dollars string is missing or malformed, so consumers don't
 * silently lose data when a payload omits the `_dollars` variant.
 */
export const centsFromDollarsString = (dollars: DollarsString | undefined, fallbackCents: number): number =>
    centsFromDollarsStringOrNull(dollars) ?? fallbackCents;

export const centsFromDollarsStringOrZero = (dollars: DollarsString | undefined): number =>
    centsFromDollarsStringOrNull(dollars) ?? 0;

/**
 * Resolve a fee total (cents) from two optional exact per-leg components,
 * preferring the exact breakdown over a proportional `estimate`. When either leg
 * is present it is treated as authoritative and the missing leg counts as `0` —
 * so a real one-sided `0` (e.g. only Yes contracts bought) is respected rather
 * than discarded for the estimate. Falls back to `estimate` only when BOTH legs
 * are absent.
 */
export const sumExactFeesOrEstimate = (exactA: number | null, exactB: number | null, estimate: number): number =>
    exactA != null || exactB != null ? (exactA ?? 0) + (exactB ?? 0) : estimate;

export const convertDollarsStringToMoney = (dollarsString?: DollarsString): string => {
    if (typeof dollarsString === 'string') {
        const dollars = parseDollarsString(dollarsString).toNumber();
        return toMoney(dollars, false);
    }
    return '$––';
};
